/**
 * Hook for managing wizard state in URL parameters
 * Enables sharing and bookmarking configurations
 */

import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { wizardStore, type WizardState } from '@/stores/wizardStore';
import { toast } from './use-toast';

export function useUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Encode wizard state to URL
  const encodeStateToUrl = useCallback((state: WizardState) => {
    try {
      const stateData = {
        env: state.environment,
        personas: state.personas,
        models: state.modelSelections.selectedModelIds,
      };
      
      const encoded = btoa(JSON.stringify(stateData));
      return encoded;
    } catch (err) {
      console.error('Failed to encode state:', err);
      return null;
    }
  }, []);

  // Decode wizard state from URL
  const decodeStateFromUrl = useCallback((encoded: string): Partial<WizardState> | null => {
    try {
      const decoded = JSON.parse(atob(encoded));
      return {
        environment: decoded.env,
        personas: decoded.personas,
        modelSelections: {
          selectedModelIds: decoded.models || [],
          personaToModels: {},
          modelToPersonas: {},
        },
      };
    } catch (err) {
      console.error('Failed to decode state:', err);
      return null;
    }
  }, []);

  // Load state from URL on mount
  useEffect(() => {
    const configParam = searchParams.get('config');
    if (configParam) {
      const decoded = decodeStateFromUrl(configParam);
      if (decoded) {
        wizardStore.setState({
          ...wizardStore.getState(),
          ...decoded,
        });
        
        toast({
          title: 'Configuration Loaded',
          description: 'Loaded configuration from shared link',
        });
        
        // Remove the config param after loading
        searchParams.delete('config');
        setSearchParams(searchParams);
      }
    }
  }, []); // Only run on mount

  // Generate shareable URL
  const generateShareUrl = useCallback(() => {
    const state = wizardStore.getState();
    const encoded = encodeStateToUrl(state);
    
    if (encoded) {
      const url = new URL(window.location.href);
      url.searchParams.set('config', encoded);
      return url.toString();
    }
    
    return null;
  }, [encodeStateToUrl]);

  // Copy share URL to clipboard
  const copyShareUrl = useCallback(async () => {
    const shareUrl = generateShareUrl();
    
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: 'Link Copied',
          description: 'Configuration link copied to clipboard',
        });
        return true;
      } catch (err) {
        console.error('Failed to copy:', err);
        toast({
          title: 'Copy Failed',
          description: 'Could not copy link to clipboard',
          variant: 'destructive',
        });
        return false;
      }
    }
    
    return false;
  }, [generateShareUrl]);

  return {
    generateShareUrl,
    copyShareUrl,
    encodeStateToUrl,
    decodeStateFromUrl,
  };
}
